import { Component, Input, OnInit, SkipSelf } from '@angular/core';
import {
  ControlContainer,
  FormGroupDirective,
  UntypedFormGroup,
} from '@angular/forms';
import { FormControlOption } from '@app/forms/subscription/definition/interface/formControlOption.interface';
import { SubscriptionFacadeService } from '@app/forms/subscription/services/subscription-facade.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { distinctUntilChanged, of, switchMap } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'ea-subscription-option-configuration',
  templateUrl: './ea-subscription-option-configuration.component.html',
  styleUrls: ['./ea-subscription-option-configuration.component.scss'],
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: (container: ControlContainer) => container,
      deps: [[new SkipSelf(), ControlContainer]],
    },
  ],
  standalone: false,
})
export class EaSubscriptionOptionConfigurationComponent implements OnInit {
  isSwisslifePer: boolean = false;
  @Input() derrors;
  options: FormControlOption[] = [];
  constructor(
    private controlContainer: FormGroupDirective,
    private subscriptionFacadeService: SubscriptionFacadeService,
  ) {}
  get form() {
    return this.controlContainer.form.get('parameters') as UntypedFormGroup;
  }

  ngOnInit() {
    const pledge = this.subscriptionFacadeService.getPledge();
    this.isSwisslifePer =
      pledge?.productType === 'PER' &&
      !!pledge?.products
        ?.filter(product => !!product?.parameters?.partnerFlux)
        ?.some(product =>
          ['SWISSLIFE'].includes(product?.parameters?.partnerFlux),
        );
    if (this.isSwisslifePer) {
      this.options = [
        {
          label: 'Investissement progressif',
          value: 'Investissement progressif',
          suffix: 'A',
          svg: 'coins.svg',
          formControlName: 'avOptionInvestissementProgressif',
        },
        {
          label: 'Arbitrage automatique',
          value: 'Arbitrage automatique',
          suffix: 'B',
          svg: 'balance-scale.svg',
          formControlName: 'avOptionReequilibrageAutomatique',
        },
      ];
    } else {
      this.options = [
        {
          label: 'Investissement progressif',
          value: 'Investissement progressif',
          suffix: 'A',
          svg: 'coins.svg',
          formControlName: 'avOptionInvestissementProgressif',
        },
        {
          label: 'Sécurisation des plus-values',
          value: 'Sécurisation des plus-values',
          suffix: 'B',
          svg: 'locked.svg',
          formControlName: 'avOptionSecurisationPlusValues',
        },
        {
          label: 'Limitation des moins-values relatives',
          value: 'Limitation des moins-values relatives',
          suffix: 'C',
          svg: 'combo-chart.svg',
          formControlName: 'avOptionLimitationMoinsValuesRelatives',
        },
        {
          label: 'Arbitrage automatique',
          value: 'Arbitrage automatique',
          suffix: 'D',
          svg: 'balance-scale.svg',
          formControlName: 'avOptionReequilibrageAutomatique',
        },
      ];
    }
    this.avProgressif();
    this.avPlusValues();
    this.avMoinsValuesRelatives();
    this.avReequilibrageAutomatique();
  }
  avProgressif() {
    this.form
      .get('avOptionInvestissementProgressif')
      .valueChanges.pipe(
        untilDestroyed(this),

        switchMap(value => {
          return of(value);
        }),

        distinctUntilChanged(),
      )
      .subscribe(value => {
        if (value) {
          this.options.forEach(option => {
            if (option.formControlName !== 'avOptionInvestissementProgressif')
              this.disabledControl(option.formControlName);
          });
        } else {
          this.options.forEach(option => {
            if (option.formControlName !== 'avOptionInvestissementProgressif')
              this.enabledControl(option.formControlName);
          });
        }
      });
  }
  avPlusValues() {
    this.form
      .get('avOptionSecurisationPlusValues')

      .valueChanges.pipe(
        untilDestroyed(this),
        switchMap(value => {
          return of(value);
        }),

        distinctUntilChanged(),
      )
      .subscribe(value => {
        if (value) {
          this.disabledControl('avOptionInvestissementProgressif');
          this.disabledControl('avOptionReequilibrageAutomatique');
        } else {
          if (!this.form.get('avOptionLimitationMoinsValuesRelatives').value) {
            this.enabledControl('avOptionInvestissementProgressif');
            this.enabledControl('avOptionReequilibrageAutomatique');
          }
        }
      });
  }
  avMoinsValuesRelatives() {
    this.form
      .get('avOptionLimitationMoinsValuesRelatives')
      .valueChanges.pipe(
        untilDestroyed(this),
        switchMap(value => {
          return of(value);
        }),

        distinctUntilChanged(),
      )
      .subscribe(value => {
        if (value) {
          this.disabledControl('avOptionInvestissementProgressif');
          this.disabledControl('avOptionReequilibrageAutomatique');
        } else {
          if (!this.form.get('avOptionSecurisationPlusValues').value) {
            this.enabledControl('avOptionInvestissementProgressif');
            this.enabledControl('avOptionReequilibrageAutomatique');
          }
        }
      });
  }
  avReequilibrageAutomatique() {
    this.form
      .get('avOptionReequilibrageAutomatique')
      .valueChanges.pipe(
        untilDestroyed(this),
        switchMap(value => {
          return of(value);
        }),

        distinctUntilChanged(),
      )
      .subscribe(value => {
        if (value) {
          this.options.forEach(option => {
            if (option.formControlName !== 'avOptionReequilibrageAutomatique')
              this.disabledControl(option.formControlName);
          });
        } else {
          this.options.forEach(option => {
            if (option.formControlName !== 'avOptionReequilibrageAutomatique')
              this.enabledControl(option.formControlName);
          });
        }
      });
  }
  disabledControl(controlName: string) {
    this.form.get(controlName).disable({
      onlySelf: true,
      emitEvent: false,
    });
    this.form.updateValueAndValidity();
  }
  enabledControl(controlName: string) {
    this.form.get(controlName).enable();
    this.form.updateValueAndValidity();
  }
}
